import { useClientSession } from '@shared/ui/src/hooks/useClientSession';
import { useMemo } from 'react';
import { PermissionVerb, StaticResource } from '@shared/utils/src/types';

/**
 * Hook for client-side permission checks
 * @param resource The resource to check permissions for
 * @param verbs The verbs to check permissions for
 * @param context Optional context for permission checking
 * @returns Object containing hasPermission boolean
 */
export function useAuthorize(
  resource: StaticResource,
  verbs: PermissionVerb[],
  context?: {
    targetUserId?: string;
    targetTenantId?: string;
  }
) {

  const session = useClientSession();
  
  const hasPermission = useMemo(() => {
    if (!session?.user?.permissions) return false;
    
    // Check if user has the permissions
    const resourcePermissions = session.user.permissions[resource] || [];
    const hasAllVerbs = verbs.every(verb => resourcePermissions.includes(verb));
    
    if (!hasAllVerbs) return false;
    
    // Check tenant access if context provided
    if (context?.targetTenantId && context.targetTenantId !== session.user.tenantId) {
      return false;
    }
    
    // Check user access if context provided
    if (context?.targetUserId && context.targetUserId !== session.user.id) {
      return false;
    }
    
    return true;
  }, [session, resource, verbs, context]);
  
  return { hasPermission };
} 